import { useEffect, useState } from "react";
import { Plus, Loader2, Save, Building2 } from "lucide-react";
import { toast } from "sonner";
import { finApi, apiErr } from "../api";
import { useFin, PageHeader, Card } from "../FinanceApp";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { InvoiceThemeCard } from "../components/InvoiceThemeCard";
import { WorkzoneLinkCard } from "../components/WorkzoneLinkCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ZatcaOnboarding } from "../components/ZatcaOnboarding";

export function FinUsers() {
  const { user, can } = useFin();
  if (!can("users.view")) return <div data-testid="fin-users-denied" className="py-20 text-center text-slate-400">ليس لديك صلاحية الاطلاع على المستخدمين</div>;
  return <FinUsersInner user={user} />;
}

function FinUsersInner({ user }) {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = async () => { setLoading(true); try { const [u, r] = await Promise.all([finApi.users(), finApi.roles()]); setRows(u); setRoles(r); } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const toggle = async (u) => { try { await finApi.updateUser(u.id, { active: !u.active }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const canManage = ["super_admin", "cfo"].includes(user?.role);
  const setRole = async (u, role) => { try { await finApi.updateUser(u.id, { role }); toast.success("تم تغيير الدور"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div data-testid="fin-users">
      <PageHeader title="المستخدمون والصلاحيات" subtitle="إدارة مستخدمي النظام وأدوارهم"
        action={canManage && <Button onClick={() => setOpen(true)} data-testid="fin-add-user" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"><Plus className="h-4 w-4" /> مستخدم جديد</Button>} />
      <Card className="overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div> : (
          <div className="overflow-x-auto"><table className="w-full text-sm text-right">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 font-semibold">الاسم</th><th className="px-3 py-2 font-semibold">البريد</th><th className="px-3 py-2 font-semibold">الدور</th><th className="px-3 py-2 font-semibold">الحالة</th></tr></thead>
            <tbody>{rows.map((u) => (
              <tr key={u.id} data-testid="fin-user-row" className="border-t border-slate-50">
                <td className="px-3 py-2 font-semibold text-slate-800">{u.name}</td>
                <td className="px-3 py-2 text-slate-500 font-mono-nums" dir="ltr">{u.email}</td>
                <td className="px-3 py-2">{canManage && u.id !== user.id ? (
                  <Select value={u.role} onValueChange={(v) => setRole(u, v)}><SelectTrigger className="h-8 w-40" data-testid={`fin-user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>) : u.role_label}</td>
                <td className="px-3 py-2">{canManage && u.id !== user.id ? <Switch checked={u.active} onCheckedChange={() => toggle(u)} /> : <span className={u.active ? "text-emerald-600" : "text-slate-400"}>{u.active ? "فعّال" : "معطّل"}</span>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <RolesGuide roles={roles} />
      <UserDialog open={open} onOpenChange={setOpen} roles={roles} onSaved={load} />
    </div>
  );
}

const KEY_ROLES = ["cfo", "accountant", "reviewer", "legal"];
function RolesGuide({ roles }) {
  const list = [...roles].sort((a, b) => (KEY_ROLES.indexOf(b.value) > -1) - (KEY_ROLES.indexOf(a.value) > -1));
  return (
    <Card className="mt-5 p-4" data-testid="fin-roles-guide">
      <h3 className="mb-3 font-bold text-slate-800">الأدوار وصلاحياتها</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {list.map((r) => (
          <div key={r.value} data-testid={`fin-role-${r.value}`} className={`rounded-xl border px-3 py-2.5 ${KEY_ROLES.includes(r.value) ? "border-emerald-200 bg-emerald-50/50" : "border-slate-100 bg-slate-50/60"}`}>
            <p className="text-sm font-bold text-slate-800">{r.label}</p><p className="mt-0.5 text-xs leading-5 text-slate-500">{r.desc}</p>
          </div>))}
      </div>
    </Card>
  );
}

function UserDialog({ open, onOpenChange, roles, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", email: "", password: "", role: "viewer" }); }, [open]);
  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !/^\d{6}$/.test(form.password)) { toast.warning("أكمل البيانات (الرمز السري 6 أرقام بالضبط)"); return; }
    setBusy(true); try { await finApi.createUser(form); toast.success("تم إنشاء المستخدم"); onOpenChange(false); onSaved(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-md">
        <DialogHeader className="text-start"><DialogTitle>مستخدم جديد</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5"><Label>الاسم</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fin-user-name" /></div>
          <div className="space-y-1.5"><Label>البريد الإلكتروني</Label><Input dir="ltr" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fin-user-email" /></div>
          <div className="space-y-1.5"><Label>كلمة المرور</Label><Input pin value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="fin-user-password" /></div>
          <div className="space-y-1.5"><Label>الدور</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="fin-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
            {roles.find((r) => r.value === form.role)?.desc && <p className="text-xs text-slate-500" data-testid="fin-user-role-desc">{roles.find((r) => r.value === form.role).desc}</p>}
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button onClick={save} disabled={busy} data-testid="fin-save-user" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LogoPicker({ value, onChange, label = "شعار الشركة", empty = "بدون شعار", tid = "fin-logo" }) {
  const pick = (f) => {
    if (!f) return;
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 400 / Math.max(img.width, img.height));
      const c = document.createElement("canvas"); c.width = img.width * k; c.height = img.height * k;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      onChange(c.toDataURL("image/png")); URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(f);
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 mt-3" data-testid={`${tid}-picker`}>
      {value ? <img src={value} alt={label} onError={() => onChange("")} className="h-14 w-24 object-contain rounded bg-slate-50" data-testid={`${tid}-preview`} /> : <div className="h-14 w-24 rounded bg-slate-100 grid place-items-center text-[10px] text-slate-400">{empty}</div>}
      <div className="flex-1 text-sm"><div className="font-semibold">{label}</div><div className="text-xs text-slate-500">PNG أو JPG — يظهر على الفواتير</div></div>
      <label className="cursor-pointer text-sm text-emerald-700 font-semibold hover:underline">اختيار<input type="file" accept="image/png,image/jpeg" className="hidden" data-testid={`${tid}-input`} onChange={(e) => pick(e.target.files?.[0])} /></label>
      {value && <button type="button" className="text-xs text-rose-600" onClick={() => onChange("")} data-testid={`${tid}-remove`}>إزالة</button>}
    </div>
  );
}

export function FinSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { finApi.settings().then(setS).catch((e) => toast.error(apiErr(e))); }, []);
  const setZ = (patch) => setS((x) => ({ ...x, zatca: { ...x.zatca, ...patch } }));
  const save = async () => {
    setBusy(true);
    try { await finApi.updateSettings({ vat_rate: parseFloat(s.vat_rate), require_approval: !!s.require_approval, zatca: s.zatca, logo: s.logo || "" }); toast.success("تم حفظ الإعدادات"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!s) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div>;
  return (
    <div data-testid="fin-settings-page">
      <PageHeader title="إعدادات الضريبة والفوترة الإلكترونية" subtitle="نسبة الضريبة وربط ZATCA (قابلة للتحديث)" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WorkzoneLinkCard />
        <Card className="p-5">
          <div className="text-sm font-bold text-slate-800 mb-3">ضريبة القيمة المضافة</div>
          <div className="space-y-1.5 max-w-[180px]"><Label>النسبة القياسية (%)</Label>
            <Input dir="ltr" type="number" value={s.vat_rate} onChange={(e) => setS({ ...s, vat_rate: e.target.value })} data-testid="fin-vat-rate-input" /></div>
          <p className="text-xs text-slate-400 mt-2">النسبة قابلة للتعديل ولا تُثبّت في الكود. الفئات: خاضعة/صفر/معفاة/خارج النطاق.</p>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3 mt-4">
            <div><div className="text-sm font-semibold">تفعيل سير الموافقات</div><div className="text-xs text-slate-500">الفواتير/الصرف/القيود تمرّ باعتماد قبل الترحيل</div></div>
            <Switch checked={!!s.require_approval} onCheckedChange={(v) => setS({ ...s, require_approval: v })} data-testid="fin-require-approval" />
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-sm font-bold text-slate-800 mb-3">الفوترة الإلكترونية ZATCA</div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3 mb-3">
            <div className="text-sm font-semibold">تفعيل التوليد</div>
            <Switch checked={!!s.zatca.enabled} onCheckedChange={(v) => setZ({ enabled: v })} data-testid="fin-zatca-enabled" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>البيئة</Label>
              <Select value={s.zatca.environment} onValueChange={(v) => setZ({ environment: v })}><SelectTrigger data-testid="fin-zatca-env"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sandbox">بيئة المطورين Sandbox</SelectItem><SelectItem value="simulation">المحاكاة Simulation</SelectItem><SelectItem value="production">إنتاج Production</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>الرقم الضريبي للبائع</Label><Input dir="ltr" value={s.zatca.vat_number || ""} onChange={(e) => setZ({ vat_number: e.target.value })} data-testid="fin-zatca-vat" /></div>
            <div className="space-y-1.5 col-span-2"><Label>الاسم التجاري للبائع</Label><Input value={s.zatca.seller_name || ""} onChange={(e) => setZ({ seller_name: e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2"><Label>الاسم التجاري بالإنجليزي (للفاتورة)</Label><Input dir="ltr" value={s.zatca.seller_name_en || ""} onChange={(e) => setZ({ seller_name_en: e.target.value })} data-testid="fin-seller-name-en" /></div>
            <div className="space-y-1.5"><Label>السجل التجاري</Label><Input dir="ltr" value={s.zatca.cr_number || ""} onChange={(e) => setZ({ cr_number: e.target.value })} data-testid="fin-seller-cr" /></div>
            <div className="space-y-1.5"><Label>المدينة</Label><Input value={s.zatca.city || ""} onChange={(e) => setZ({ city: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>العنوان</Label><Input value={s.zatca.address || ""} onChange={(e) => setZ({ address: e.target.value })} /></div>
            <div className="col-span-2 mt-1 text-xs font-bold text-slate-600">العنوان الوطني (إلزامي في الفاتورة الضريبية)</div>
            {[["street", "اسم الشارع"], ["building_number", "رقم المبنى (4 أرقام)"], ["additional_number", "الرقم الإضافي"], ["district", "الحي"], ["postal_code", "الرمز البريدي (5 أرقام)"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={s.zatca[k] || ""} onChange={(e) => setZ({ [k]: e.target.value })} data-testid={`fin-seller-${k}`} /></div>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3 mt-3">
            <div className="text-sm">QR المرحلة الثانية (9 حقول: الهاش والتوقيع والمفتاح العام) يُولَّد تلقائياً مع كل فاتورة موقّعة</div>
            <Switch checked={!!s.zatca.include_hash_in_qr} onCheckedChange={(v) => setZ({ include_hash_in_qr: v })} />
          </div>
          <LogoPicker label="شعار الشركة على الفواتير" value={s.logo} onChange={(logo) => setS({ ...s, logo })} />
          <p className="text-xs text-slate-400 mt-2">الإرسال المباشر (Clearance/Reporting) يتم بعد إكمال خطوات الربط أدناه. احفظ الإعدادات قبل توليد CSR.</p>
        </Card>
      </div>
      <ZatcaOnboarding environment={s.zatca.environment} />
      <div className="mt-4"><Button onClick={save} disabled={busy} data-testid="fin-save-settings" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ الإعدادات</Button></div>
    </div>
  );
}

const BANK0 = { bank_name: "", account_name: "", account_number: "", iban: "", show_on_invoice: true };
function BankAccounts({ list, onChange }) {
  const set = (i, p) => onChange(list.map((b, k) => (k === i ? { ...b, ...p } : b)));
  return (
    <div className="mt-4" data-testid="fin-bank-accounts">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-bold text-slate-800">الحسابات البنكية</div>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...list, { ...BANK0 }])} className="gap-1" data-testid="fin-bank-add"><Plus className="h-4 w-4" /> إضافة حساب</Button>
      </div>
      {list.length === 0 && <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">لا توجد حسابات بنكية — أضف حساباً ليظهر على الفواتير</p>}
      <div className="space-y-2">{list.map((b, i) => (
        <div key={i} className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 p-3 sm:grid-cols-2" data-testid={`fin-bank-${i}`}>
          <Input placeholder="اسم البنك (مثال: مصرف الراجحي)" value={b.bank_name} onChange={(e) => set(i, { bank_name: e.target.value })} data-testid={`fin-bank-name-${i}`} />
          <Input placeholder="اسم صاحب الحساب" value={b.account_name} onChange={(e) => set(i, { account_name: e.target.value })} data-testid={`fin-bank-holder-${i}`} />
          <Input dir="ltr" placeholder="رقم الحساب" value={b.account_number} onChange={(e) => set(i, { account_number: e.target.value })} data-testid={`fin-bank-number-${i}`} />
          <Input dir="ltr" placeholder="SA00 0000 0000 0000 0000 0000" value={b.iban} onChange={(e) => set(i, { iban: e.target.value.toUpperCase() })} data-testid={`fin-bank-iban-${i}`} />
          <div className="flex items-center justify-between sm:col-span-2">
            <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={b.show_on_invoice !== false} onCheckedChange={(v) => set(i, { show_on_invoice: v })} data-testid={`fin-bank-show-${i}`} /> إظهار على الفواتير</label>
            <button type="button" onClick={() => onChange(list.filter((_, k) => k !== i))} className="text-xs text-rose-600" data-testid={`fin-bank-remove-${i}`}>حذف</button>
          </div>
        </div>
      ))}</div>
    </div>
  );
}

export function FinCompany() {
  const { user, setUser } = useFin();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    finApi.companies().then((d) => setCompanies(d.companies || [])).catch(() => {});
    finApi.settings().then((s) => setLogo(s.logo || "")).catch(() => {});
    if (user?.company) setForm({ bank_accounts: [], stamp: "", ...user.company });
  }, [user]);
  const save = async () => {
    setBusy(true);
    try {
      await finApi.updateCompany(form.id, form);
      if (logo !== null) await finApi.updateSettings({ logo });
      const me = await finApi.me(); setUser(me); toast.success("تم حفظ بيانات الشركة");
    }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const switchTo = async (id) => { try { await finApi.switchCompany(id); const me = await finApi.me(); setUser(me); toast.success("تم تبديل الشركة"); } catch (e) { toast.error(apiErr(e)); } };
  if (!form) return null;
  return (
    <div data-testid="fin-company">
      <PageHeader title="بيانات الشركة" subtitle="المعلومات القانونية والضريبية للشركة" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2"><Label>الاسم القانوني</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fin-company-name-input" /></div>
            <div className="space-y-1.5"><Label>الرقم الضريبي (VAT)</Label><Input dir="ltr" value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid="fin-company-vat" /></div>
            <div className="space-y-1.5"><Label>السجل التجاري (CR)</Label><Input dir="ltr" value={form.cr_number || ""} onChange={(e) => setForm({ ...form, cr_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>المدينة</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>الهاتف</Label><Input dir="ltr" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2"><Label>العنوان</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>العملة الأساسية</Label><Input value={form.base_currency || "SAR"} disabled dir="ltr" /></div>
          </div>
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <LogoPicker label="شعار الشركة" value={logo || ""} onChange={setLogo} tid="fin-company-logo" />
            <LogoPicker label="ختم الشركة" empty="بدون ختم" value={form.stamp || ""} onChange={(stamp) => setForm({ ...form, stamp })} tid="fin-company-stamp" />
          </div>
          <BankAccounts list={form.bank_accounts || []} onChange={(bank_accounts) => setForm({ ...form, bank_accounts })} />
          <div className="mt-4"><Button onClick={save} disabled={busy} data-testid="fin-save-company" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ</Button></div>
        </Card>
        <Card className="p-5">
          <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Building2 className="h-4 w-4" /> الشركات</div>
          <div className="space-y-2">{companies.map((c) => (
            <button key={c.id} onClick={() => switchTo(c.id)} data-testid={`fin-company-switch-${c.id}`}
              className={`w-full text-right rounded-xl border p-3 text-sm transition ${c.id === user.company_id ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="font-semibold text-slate-800">{c.name}</div>
              {c.id === user.company_id && <div className="text-[11px] text-emerald-600">الشركة النشطة</div>}
            </button>
          ))}</div>
        </Card>
      </div>
      <Card className="p-5 mt-4"><InvoiceThemeCard /></Card>
    </div>
  );
}
